import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import useDutyLeaveRuleApi from './config';
import { useAuth } from '../../context/AuthContext';
import DataTable from '../../components/ui/DataTable';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import OptionsMenu from '../../components/ui/OptionsMenu';

// Nöbet İzni Kuralları (AKM POC §6.5) — TitlesPage aynası. Başlık yalnız DataTable `title`'ında, "Yeni"
// tablonun içinde. Liste ucu sayfasız (en çok 3 kural) → istemci tarafı tablo.
const Container = styled.div``;

const InfoNote = styled.div`
  background: #fff7ed; border: 1px solid #fed7aa; color: #9a3412;
  border-radius: 6px; padding: 10px 12px; font-size: 13px; margin: 0 0 12px; line-height: 1.4;
`;

const EMPTY_CONFIRM = { isOpen: false, id: null, name: '' };

export default function DutyLeaveRulesPage() {
  const { t } = useTranslation();
  const { canWrite } = useAuth();
  const writable = canWrite('SM');

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(EMPTY_CONFIRM);
  const [deleting, setDeleting] = useState(false);

  const {
    objectSchema, fetching, loadFailed, columns: baseColumns, data, create, update, _delete,
  } = useDutyLeaveRuleApi();

  const handleNewButtonClick = useCallback(() => {
    setSelectedRow(null);
    setDrawerOpen(true);
  }, []);

  const handleEdit = useCallback((row) => {
    setSelectedRow({ ...row });
    setDrawerOpen(true);
  }, []);

  const handleSave = useCallback(async (formData) => {
    const success = selectedRow
      ? await update(selectedRow.id, formData)
      : await create(formData);
    if (success) setDrawerOpen(false);
    return success;
  }, [selectedRow, create, update]);

  const handleDelete = useCallback((row) => {
    setDeleteConfirm({ isOpen: true, id: row.id, name: row.name });
  }, []);

  // Silme reddedilirse (toast interceptor'da) onay penceresi açık kalır.
  const handleDeleteConfirm = useCallback(async () => {
    setDeleting(true);
    const ok = await _delete(deleteConfirm.id);
    setDeleting(false);
    if (ok) setDeleteConfirm(EMPTY_CONFIRM);
  }, [_delete, deleteConfirm.id]);

  const actionOptions = useMemo(() => {
    if (!writable) return [];
    return [
      { type: 'edit', label: t('dutyLeaveRule.edit'), onClick: handleEdit },
      { type: 'divider' },
      { type: 'delete', label: t('dutyLeaveRule.delete'), onClick: handleDelete },
    ];
  }, [t, writable, handleEdit, handleDelete]);

  const columns = useMemo(() => {
    if (actionOptions.length === 0) return baseColumns;
    return [
      ...baseColumns,
      {
        name: 'actions',
        label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            return <OptionsMenu actions={actionOptions} row={row} />;
          },
        },
      },
    ];
  }, [baseColumns, actionOptions, data]);

  return (
    <Container>
      <GenericFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={selectedRow ? t('dutyLeaveRule.edit') : t('dutyLeaveRule.new')}
        schema={objectSchema}
        initialValues={selectedRow}
        onSave={handleSave}
      />

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title={t('dutyLeaveRule.delete')}
        message={t('dutyLeaveRule.deleteMessage', { name: deleteConfirm.name })}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirm(EMPTY_CONFIRM)}
        loading={deleting}
      />

      {loadFailed && (
        <InfoNote role="alert" data-testid="duty-leave-rule-load-failed">{t('dutyLeaveRule.loadFailed')}</InfoNote>
      )}

      {useMemo(() => (
        <DataTable
          title={t('dutyLeaveRule.title')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={data.length}
          pageSize={10}
          serverSide={false}
          disableFilters
          canCreateNewRecord={writable}
          newButtonLabel={t('dutyLeaveRule.new')}
          onNewButtonClick={handleNewButtonClick}
        />
      ), [t, fetching, columns, data, writable, handleNewButtonClick])}
    </Container>
  );
}
